import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import CustomModal from "../../components/common/customModal";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { SearchResultItem, TableColumn } from "../../types/dashboard";
import { styled } from "@mui/material/styles";
import RE_Logo from "../../assets/images/RE_Logo.svg";
import dayjs from "dayjs";
import { useAppDispatch } from "../../globalState/hooks";
import { setDashboardSearchKey } from "../../globalState/actions/dashboard/dashboardAction";

interface DashboardSearchModalProps {
  open: boolean;
  onClose: () => void;
  searchResult: SearchResultItem[];
}

const DashboardSearchModal: React.FC<DashboardSearchModalProps> = ({
  open,
  onClose,
  searchResult,
}) => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (open) {
      dispatch(setDashboardSearchKey(""));
    }
  }, [dispatch, open]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#000000",
      color: theme.palette.common.white,
      fontWeight: "bold",
      whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    border: "none",
    width: "fit-content",
    minWidth: "100px",
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    border: "none",
  }));

  const columns: TableColumn[] = [
    { id: "fullName", label: "Employee Name" },
    { id: "userId", label: "Employee Code" },
    { id: "dateOfBirth", label: "Date Of Birth" },
    { id: "gender", label: "Gender" },
    { id: "stateName", label: "State Name" },
    { id: "locationName", label: "Location Name" },
    { id: "department", label: "Department Name" },
    { id: "managerName", label: "Manager Name" },
    { id: "costCenter", label: "Cost Center" },
    { id: "businessUnitName", label: "Business Manager" },
    { id: "dateOfJoining", label: "Date Of Joining" },
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      title="Search Contact"
      width={1000}
      onSave={() => {}}
      btnLoading={false}
      isSaveButtonShow={false}
    >
      <Box mt={1}>
        <TableContainer>
          <Table
            stickyHeader
            aria-label="sticky table"
            style={{
              width: "100%",
              fontSize: "14px",
              color: "#ffffff",
              overflow: "scroll",
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell key={column.id}>
                    {column.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {searchResult
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                  >
                    {columns.map((column) => (
                      <StyledTableCell
                        align="left"
                        key={column.id}
                        sx={{ color: "#ffffff" }}
                      >
                        {column.id === "fullName" ? (
                          <Box display="flex" alignItems="center">
                            <Avatar
                              src={row.photoUrl || RE_Logo}
                              alt={row.fullName}
                              sx={{ width: 40, height: 40, mr: 1 }}
                            />
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{ color: "#ffffff" }}
                              >
                                {row.fullName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#ffffff" }}
                              >
                                {row.email}
                              </Typography>
                            </Box>
                          </Box>
                        ) : column.id === "dateOfBirth" ? (
                          row.dateOfBirth ? (
                            dayjs(row.dateOfBirth).format("DD-MMM-YYYY")
                          ) : (
                            "-"
                          )
                        ) : column.id === "dateOfJoining" ? (
                          row.dateOfJoining ? (
                            dayjs(row.dateOfJoining).format("DD-MMM-YYYY")
                          ) : (
                            "-"
                          )
                        ) : (
                          row[column.id as keyof SearchResultItem] ?? "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={searchResult.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: "#ffffff",
            "& .MuiSelect-icon": { color: "#ffffff" },
            "& .MuiSvgIcon-root": { color: "#ffffff" },
          }}
        />
      </Box>
    </CustomModal>
  );
};

export default DashboardSearchModal;
